import { useMemo, useState } from "react";
import { View } from "react-native";
import { CommonActions, useNavigation } from "@react-navigation/native";
import { FlaskConical, Plus } from "lucide-react-native";
import { SectionTitle } from "@louise/moon-ui/primitives/SectionTitle";
import { EMBER } from "../../../../onboarding/theme";
import { getBiomarkerFactsheet } from "@src/packages/test-results/content/biomarker-factsheets";
import { groupCanonicalTestResults } from "@src/packages/test-results/canonical-results";
import type { CanonicalTestResult } from "@src/packages/test-results/canonical-results";
import {
  useDeleteTestResult,
  useUpdateTestResult,
  type TestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import type { LabResult } from "./ResultsLabsCard";
import TestResultDetailDialog from "../../../components/TestResultDetailDialog";
import ViewAllLink from "../../../components/ViewAllLink";
import {
  DocumentRow,
  DocumentTestResultRow,
  documentTestTubesIcon,
  type DocumentRowBadge,
  type DocumentTestResultCategory,
} from "../../documents/DocumentRow";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { formatDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";
import type { MetricCycleLink } from "../../../components/MetricDetailSheet";
import {
  getCanonicalTestIdForDataPoint,
  getCanonicalTestIdForMarkerName,
  getTestRouteTarget,
} from "../../access/testRouteIds";
import {
  getTestResultInterpretationContext,
  isCycleContextSensitiveTest,
  resolveResultInterpretationContext,
  type ResultPhaseLike,
} from "@src/packages/test-results/interpretation-context";

type Props = {
  testResults: TestResult[];
  onViewAll: () => void;
  onAddTestResult: () => void;
  title?: string;
  cycleLink?: MetricCycleLink;
  onOpenCycle?: (cycleLink: MetricCycleLink) => void;
  cycleStartDate?: string | null;
  phases?: ResultPhaseLike[];
};

export default function CycleTestsSection({
  testResults,
  onViewAll,
  onAddTestResult,
  title,
  cycleLink,
  onOpenCycle,
  cycleStartDate,
  phases = [],
}: Props) {
  const t = useTranslations();
  const navigation = useNavigation();
  const { locale } = useLocale();
  const sectionTitle = title ?? t("care.cycle_tests.section_title");
  const [selectedResult, setSelectedResult] = useState<LabResult | null>(null);
  const deleteTestResult = useDeleteTestResult();
  const updateTestResult = useUpdateTestResult();
  const openTestFactsheet = (testId: string) => {
    setSelectedResult(null);
    const target = getTestRouteTarget(testId);
    navigation.getParent()?.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: {
          screen: target.screen,
          params: { ...target.params, returnTab: "Care" },
        },
      }),
    );
  };
  const labResults = useMemo(
    () =>
      buildLabResultsFromTestResults(
        testResults,
        locale,
        cycleLink,
        cycleStartDate,
        phases,
      ),
    [cycleLink, cycleStartDate, locale, phases, testResults],
  );
  const previewResults = labResults.slice(0, 3);

  return (
    <>
      <View
        style={{
          marginTop: 28,
          marginBottom: 10,
          marginLeft: 4,
          marginRight: 2,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <SectionTitle icon={FlaskConical}>{sectionTitle}</SectionTitle>
        <ViewAllLink
          label={t("care.cycle_tests.view_all")}
          onPress={onViewAll}
        />
      </View>

      <View style={{ gap: 9 }}>
        {previewResults.length > 0 ? (
          previewResults.map((result) => {
            const pending = isLabResultPending(result);
            return (
              <DocumentTestResultRow
                key={result.id}
                title={result.name}
                subtitle={getLabResultSubtitle(result, t)}
                dateLabel={getLabResultDateLabel(result, t)}
                value={result.value || t("care.cycle_tests.see_details")}
                unit={result.unit}
                category={getLabResultCategory(result)}
                badge={getLabResultRowBadge(result, t)}
                pending={pending}
                pendingOpacity={result.status === "scheduled" ? 0.2 : 1}
                onPress={() => setSelectedResult(result)}
              />
            );
          })
        ) : (
          <DocumentRow
            title={t("care.cycle_tests.empty_title")}
            subtitle={t("care.cycle_tests.empty_subtitle")}
            imageSource={documentTestTubesIcon}
            trailing={
              <Plus
                size={18}
                color={EMBER.colors.textLight}
                strokeWidth={1.5}
              />
            }
            onPress={onAddTestResult}
            showChevron={false}
          />
        )}
      </View>

      <TestResultDetailDialog
        result={selectedResult}
        point={
          selectedResult
            ? {
                id: selectedResult.id,
                date: selectedResult.date,
                value: selectedResult.value,
                clinicalAssessment: selectedResult.clinicalAssessment,
                clinicalAssessmentDetails:
                  selectedResult.clinicalAssessmentDetails,
                attachment: selectedResult.attachment,
                provenance: selectedResult.provenance,
              }
            : null
        }
        onClose={() => setSelectedResult(null)}
        onEdit={async (id, data) => {
          await updateTestResult.mutateAsync({ testResultId: id, data });
          setSelectedResult(null);
        }}
        onDelete={async (id) => {
          await deleteTestResult.mutateAsync(id);
          setSelectedResult(null);
        }}
        onAttachSource={async (id, attachmentId) => {
          await updateTestResult.mutateAsync({
            testResultId: id,
            data: { attachmentId },
          });
        }}
        cycleLink={selectedResult?.cycleLink}
        onOpenCycle={onOpenCycle}
        testId={selectedResult?.testId}
        onOpenTest={openTestFactsheet}
      />
    </>
  );
}

function formatTestLabel(value: string | null | undefined) {
  if (!value) return null;
  return value
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getTestResultKindLabel(
  result: Pick<CanonicalTestResult, "type" | "category">,
) {
  const type = formatTestLabel(result.type);
  const category = formatTestLabel(result.category);

  return [type, category].filter(Boolean).join(" · ");
}

function getLabResultCategory(result: LabResult): DocumentTestResultCategory {
  if (result.isAbnormal) return "borderline";
  if (result.trend === "up") return "high";
  if (result.trend === "down") return "low";
  return "normal";
}

function getLabResultRowBadge(
  result: LabResult,
  t: TranslationFunction,
): DocumentRowBadge | undefined {
  if (result.status === "waiting" || /waiting/i.test(result.date)) {
    return { label: t("care.cycle_tests.badge_waiting"), tone: "gold" };
  }
  if (result.status === "scheduled" || /scheduled/i.test(result.date)) {
    return { label: t("care.cycle_tests.badge_scheduled"), tone: "neutral" };
  }
  return undefined;
}

function isLabResultPending(result: LabResult) {
  return (
    result.status === "waiting" ||
    result.status === "scheduled" ||
    /waiting|scheduled/i.test(result.date)
  );
}

function getLabResultDateLabel(result: LabResult, t: TranslationFunction) {
  const date = result.date.trim();
  if (!date || /waiting|scheduled/i.test(date)) {
    return t("care.cycle_tests.no_date");
  }
  return date;
}

function getLabResultSubtitle(result: LabResult, t: TranslationFunction) {
  if (result.description) return result.description;
  if (result.appointmentLabel) return result.appointmentLabel;
  return t("care.cycle_tests.linked_to_cycle");
}

function buildLabResultsFromTestResults(
  results: TestResult[],
  locale: Locale,
  cycleLink?: MetricCycleLink,
  cycleStartDate?: string | null,
  phases: ResultPhaseLike[] = [],
): LabResult[] {
  return groupCanonicalTestResults(results)
    .map((historyGroup) => {
      const latest = historyGroup.latest;
      const name = latest.name ?? "Unknown result";
      const factsheet = getBiomarkerFactsheet(name, locale);
      const interpretationContext = isCycleContextSensitiveTest(name)
        ? getTestResultInterpretationContext(
            name,
            resolveResultInterpretationContext({
              resultDate: latest.date,
              cycleStartDate,
              phaseId: latest.phaseId,
              phases,
            }),
          )
        : undefined;
      const dateLabel = latest.date ? formatShortDate(latest.date, locale) : "";

      return {
        id: latest.id,
        dataPointId: historyGroup.dataPointId,
        testId:
          getCanonicalTestIdForDataPoint(historyGroup.dataPointId) ??
          getCanonicalTestIdForMarkerName(name),
        name,
        value: latest.value,
        unit: latest.unit ?? "",
        date: dateLabel,
        cycleId: latest.cycleId,
        ...(factsheet ?? {}),
        trend: historyGroup.trend,
        isAbnormal:
          latest.clinicalAssessment === "low" ||
          latest.clinicalAssessment === "high",
        attachment: latest.source.attachment,
        provenance: latest.source,
        history:
          historyGroup.results.length > 1
            ? historyGroup.results.map((item) => ({
                id: item.id,
                date: item.date ? formatShortDate(item.date, locale) : "",
                value: item.value,
                clinicalAssessment: item.clinicalAssessment,
                clinicalAssessmentDetails: item.clinicalAssessmentDetails,
                attachment: item.source.attachment,
                provenance: item.source,
              }))
            : undefined,
        appointmentLabel: getTestResultKindLabel(latest),
        interpretationContext,
        cycleLink,
      };
    })
    .sort(
      (a, b) =>
        new Date(b.date || "").getTime() - new Date(a.date || "").getTime(),
    );
}

function formatShortDate(dateStr: string | null, locale: Locale): string {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return formatDate(date, locale, { month: "short", day: "numeric" });
}
